import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";

import type { IMessage, IPrompt } from "./types";
import { useChatStore } from "./use-chat-store";
import { useSendMessage } from "./use-connection";

import { useCurrentOrganization } from "hooks/use-current-organization";

interface IWebSocket {
  handleResetConversation: () => void;
}

const useWebSocket = (): IWebSocket => {
  const urlParams = useParams();
  const organizationName = useCurrentOrganization();
  const { setMessages, setIsLoading, setError, setPrompt, prompt } =
    useChatStore();
  const sendMessage = useSendMessage();

  const handleResetConversation = useCallback((): void => {
    setIsLoading(true);

    sendMessage(
      JSON.stringify({
        organizationName,
        type: "reset_conversation_context",
      }),
    );
  }, [organizationName, setIsLoading, sendMessage]);

  const sendUserMessage = useCallback(
    (message: IPrompt): void => {
      setError(null);
      setIsLoading(true);
      setMessages([{ content: message.prompt, role: "user" }]);
      sendMessage(
        JSON.stringify({
          metadata: urlParams,
          organizationName,
          prompt: message.prompt,
          type: "prompt",
        }),
      );
      setPrompt(null);
    },
    [
      urlParams,
      organizationName,
      setIsLoading,
      setMessages,
      setError,
      setPrompt,
      sendMessage,
    ],
  );

  useEffect((): void => {
    if (prompt !== null && prompt.trim() !== "") {
      sendUserMessage({ prompt });
    }
  }, [prompt, sendUserMessage]);

  return { handleResetConversation };
};

export { useWebSocket };
export type { IMessage };
